import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { io } from "socket.io-client";
import { auth } from "../firebase/firebase";

const WebSocketContext = createContext(null);

export const WebSocketProvider = ({ user, children }) => {
  const [socket, setSocket] = useState(null);
  const socketBase = useMemo(() => {
    const fallbackOrigin =
      typeof window !== "undefined" ? window.location.origin : "";
    return (
      process.env.REACT_APP_SOCKET_URL ||
      fallbackOrigin ||
      "http://localhost:5001"
    );
  }, []);

  useEffect(() => {
    // Debug: log when useEffect runs and current user id
    try {
      console.info("[WS] useEffect trigger", { userId: user?.id, socketBase });
    } catch (e) {}

    // Don't connect until we have a user ID
    if (!user?.id) {
      try {
        console.info("[WS] skipping connect - no user id available yet");
      } catch (e) {}
      return;
    }

    let cancelled = false;
    let socketInstance = null;

    // Fetch a fresh Firebase ID token for the socket handshake. The server
    // verifies this token and derives identity from it — the userId we also
    // send is only used for room membership AFTER verification.
    const fetchToken = async () => {
      try {
        const firebaseUser = auth.currentUser;
        if (!firebaseUser) return null;
        return await firebaseUser.getIdToken();
      } catch (e) {
        console.warn("[WS] Failed to fetch Firebase ID token", e?.message || e);
        return null;
      }
    };

    const connect = async () => {
      const token = await fetchToken();
      if (cancelled) return;

      if (!token) {
        console.warn("[WS] No Firebase token available - skipping socket connection");
        return;
      }

      // Create the socket connection, passing the verified token + userId for room membership
      const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      console.info("[WS] connecting with timezone", { userId: user.id, userTimeZone });
      socketInstance = io(socketBase, {
        withCredentials: true,
        auth: {
          token,
          userId: user.id,
          timeZone: userTimeZone,
        },
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        reconnectionAttempts: Infinity,
        timeout: 20000,
      });

      setSocket(socketInstance);

      // Basic lifecycle logging to help while learning
      socketInstance.on("connect", () => {
        console.info("[WS] connected", socketInstance.id);
        socketInstance.emit("join", { userId: user.id });

        // Always attempt to join both the user room and the linguist room for this id.
        // Joining linguist:<id> is harmless for members and ensures linguist-targeted
        // socket events reach the client even if Role metadata is missing.
        try {
          socketInstance.emit("join", { linguistId: user.id });
          console.info(`[WS] requested join linguist:${user.id}`);
        } catch (err) {
          console.warn("[WS] failed to request join linguist room", err?.message || err);
        }

        // Expose socket to window for local debugging (dev only)
        try {
          if (typeof window !== "undefined") {
            window.__socketInstance = socketInstance;
          }
        } catch (e) {
          /* ignore */
        }
      });

      socketInstance.on("disconnect", (reason) => {
        console.info("[WS] disconnected", reason);
      });

      socketInstance.on("connect_error", (err) => {
        console.error("[WS] connect_error", err.message);
        // If the server rejected our token, stop hammering it with retries.
        if (err.message && err.message.includes("Unauthorized")) {
          console.warn("[WS] Socket auth rejected - stopping reconnection attempts");
          socketInstance?.disconnect();
        }
      });

      socketInstance.on("reconnect_attempt", async (attemptNumber) => {
        console.info("[WS] reconnect attempt", attemptNumber);
        // Refresh the ID token before each attempt so an expired token
        // doesn't keep the connection from being re-established.
        try {
          const freshToken = await auth.currentUser?.getIdToken();
          if (freshToken && socketInstance) {
            socketInstance.auth = { ...socketInstance.auth, token: freshToken };
          }
        } catch (e) {
          console.warn("[WS] failed to refresh token for reconnect", e?.message || e);
        }
      });

      socketInstance.on("reconnect_failed", () => {
        console.error("[WS] reconnection failed after all attempts");
      });
    };

    connect();

    // Clean up on unmount
    return () => {
      cancelled = true;
      try {
        if (typeof window !== "undefined" && window.__socketInstance === socketInstance) {
          delete window.__socketInstance;
        }
      } catch (e) {}
      socketInstance?.disconnect();
    };
  }, [user?.id, socketBase]);

  const value = useMemo(() => ({ socket }), [socket]);

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = () => useContext(WebSocketContext);
